/**
 * school.js
 * ---------------------------------------------------------------------------
 * Achte Schwesterdatei der Reihe: identische Logik, eigener Namensraum (sc-).
 *
 * Erzeugt die fliegenden Bücher und Bleistifte im Hintergrund und lässt sie
 * umherziehen. Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius
 * Pixel, stieben sie auseinander und beruhigen sich danach wieder auf ihr
 * Reisetempo. Das Laub und die Fenster laufen rein über CSS.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Mond und Schulhof.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initSchool } from "./school.js";
 *   initSchool();
 *
 * Aktiviert wird jedes Element mit der Klasse .sc-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-school.
 */
export function school() {
    "use strict";

    var CONFIG = {
        books:       5,
        pencils:     6,
        fleeRadius:  170,
        fleeForce:   1.1,
        maxSpeed:    3.6,
        bookSpeed:   0.6,
        pencilSpeed: 0.34,
        wander:      0.05,
        calmDown:    0.012,
        margin:      70,
        bookSize:    [20, 34],
        pencilSize:  [16, 30]
    };

    var COVER_COLORS = ["#c2453f", "#2f6fa8", "#3f8f6a", "#a8722f", "#6b4a9c"];

    var BOOK =
        '<span class="sc-critter__body">' +
        '<i class="sc-cover sc-cover--l"></i>' +
        '<i class="sc-spine"></i>' +
        '<i class="sc-cover sc-cover--r"></i>' +
        '</span>';

    var PENCIL = '<span class="sc-critter__body"><i></i><i></i><i></i></span>';

    function reducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function rand(min, max) { return min + Math.random() * (max - min); }
    function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

    function create(scene) {
        var layer = scene.querySelector(".sc-critters");
        if (!layer) return;

        var W = window.innerWidth;
        var H = window.innerHeight;
        var small = W < 700;
        var critters = [];
        var pointer = { x: -9999, y: -9999, active: false };
        var running = true;
        var last = 0;

        function spawn(kind, count) {
            var isBook = kind === "book";
            var range = isBook ? CONFIG.bookSize : CONFIG.pencilSize;

            for (var i = 0; i < count; i++) {
                var size = rand(range[0], range[1]);
                var el = document.createElement("span");
                el.className = "sc-critter sc-critter--" + kind;
                el.innerHTML = isBook ? BOOK : PENCIL;
                el.style.setProperty("--sc-size", size.toFixed(1) + "px");
                if (isBook) el.style.setProperty("--sc-cover", pick(COVER_COLORS));
                el.style.opacity = rand(0.8, 1).toFixed(2);
                if (!isBook) {
                    el.querySelector(".sc-critter__body").style.animationDelay =
                        (-rand(0, 7)).toFixed(2) + "s";
                }
                layer.appendChild(el);

                var dir = rand(0, Math.PI * 2);
                var base = (isBook ? CONFIG.bookSpeed : CONFIG.pencilSpeed) * rand(0.75, 1.3);

                critters.push({
                    el: el,
                    kind: kind,
                    w: size,
                    h: isBook ? size / 1.5 : size,
                    x: rand(0, W),
                    y: rand(0, H * 0.8),
                    vx: Math.cos(dir) * base,
                    vy: Math.sin(dir) * base,
                    base: base,
                    flee: 0,
                    face: 1,
                    fleeing: false
                });
            }
        }

        spawn("book", small ? Math.ceil(CONFIG.books / 2) : CONFIG.books);
        spawn("pencil", small ? Math.ceil(CONFIG.pencils / 2) : CONFIG.pencils);

        function step(now) {
            if (!running) return;
            var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
            last = now;

            for (var i = 0; i < critters.length; i++) {
                var c = critters[i];

                c.vx += rand(-1, 1) * CONFIG.wander * dt;
                c.vy += rand(-1, 1) * CONFIG.wander * dt;

                if (pointer.active) {
                    var dx = c.x - pointer.x;
                    var dy = c.y - pointer.y;
                    var d = Math.sqrt(dx * dx + dy * dy);
                    if (d < CONFIG.fleeRadius) {
                        var push = 1 - d / CONFIG.fleeRadius;
                        var inv = 1 / (d || 1);
                        c.vx += dx * inv * CONFIG.fleeForce * push * dt;
                        c.vy += dy * inv * CONFIG.fleeForce * push * dt;
                        c.flee = 1;
                    }
                }
                c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

                if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
                else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
                if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
                else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

                var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
                var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
                var next = speed + (target - speed) * 0.05 * dt;
                c.vx = c.vx / speed * next;
                c.vy = c.vy / speed * next;

                c.x += c.vx * dt;
                c.y += c.vy * dt;
                if (c.x < -c.w) c.x = W + c.w;
                else if (c.x > W + c.w) c.x = -c.w;
                if (c.y < -c.h) c.y = H + c.h;
                else if (c.y > H + c.h) c.y = -c.h;

                c.el.style.transform =
                    "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
                    (c.y - c.h / 2).toFixed(1) + "px,0)";

                if (c.kind === "book") {
                    var face = c.vx < 0 ? -1 : 1;
                    if (face !== c.face) {
                        c.face = face;
                        c.el.style.setProperty("--sc-face", face);
                    }
                }

                var fleeing = c.flee > 0.2;
                if (fleeing !== c.fleeing) {
                    c.fleeing = fleeing;
                    c.el.classList.toggle("is-fleeing", fleeing);
                }
            }

            requestAnimationFrame(step);
        }

        window.addEventListener("pointermove", function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
        }, { passive: true });

        document.addEventListener("pointerleave", function () {
            pointer.active = false;
        });

        window.addEventListener("resize", function () {
            W = window.innerWidth;
            H = window.innerHeight;
        }, { passive: true });

        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                running = false;
            } else if (!running) {
                running = true;
                last = 0;
                requestAnimationFrame(step);
            }
        });

        requestAnimationFrame(step);
    }

    function init(scene) {
        if (scene.dataset.scReady) return;
        scene.dataset.scReady = "1";

        window.addEventListener("pointermove", function (e) {
            scene.style.setProperty("--sc-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
            scene.style.setProperty("--sc-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
        }, { passive: true });

        if (!reducedMotion()) create(scene);
    }

    function boot() {
        var scenes = document.querySelectorAll(".sc-scene");
        if (!scenes.length) return;

        document.body.classList.add("is-school");

        Array.prototype.forEach.call(scenes, init);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
}